// Excel cari dekontu ayrıştırma — farklı şablonlar için otomatik başlık/sütun tanıma + kullanıcı eşlemesi.
// Kural: eksik/anlaşılmayan hücre UYDURULMAZ (boş kalır); şüpheli veri (sıra dışı tarih, tutar uyuşmazlığı, belirsiz gün/ay) uyarı olarak raporlanır.
// Tarihler saat diliminden BAĞIMSIZ okunur (Excel seri no → gün/ay/yıl); Excel'de ne yazıyorsa o gün aktarılır.
import * as XLSX from "xlsx";

export const FIELDS = [
  ["", "— Yok —"], ["date", "Tarih"], ["name", "Ürün Adı"], ["code", "Ürün Kodu"], ["unit", "Birim"], ["qty", "Miktar"],
  ["price", "Birim Fiyat"], ["disc", "İskonto"], ["total", "Tutar"], ["pay_date", "Ödeme Tarihi"], ["pay_amount", "Ödeme Tutarı"],
];
export const FIELD_LABEL = Object.fromEntries(FIELDS);
const lc = (s) => String(s ?? "").trim().toLocaleLowerCase("tr");
const txt = (v) => String(v ?? "").trim();
export const colLetter = (i) => { let s = ""; i += 1; while (i > 0) { const m = (i - 1) % 26; s = String.fromCharCode(65 + m) + s; i = Math.floor((i - 1) / 26); } return s; };
const E = (text) => ({ sev: "error", text });
const W = (text) => ({ sev: "warn", text });
const I = (text) => ({ sev: "info", text });
export const fmtNum = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Tarih hücreleri UTC-gece yarısı Date işareti olarak taşınır (saat dilimi kayması yok). */
const utcDate = (y, m, d) => new Date(Date.UTC(y, m - 1, d));
const cellValue = (c, date1904) => {
  if (!c || c.t === "e" || c.v == null) return null;
  if (c.t === "n") {
    if (c.z && XLSX.SSF.is_date(c.z)) { const p = XLSX.SSF.parse_date_code(c.v, { date1904 }); return p ? utcDate(p.y, p.m, p.d) : c.v; }
    return c.v;
  }
  if (c.t === "d" && c.v instanceof Date) return utcDate(c.v.getFullYear(), c.v.getMonth() + 1, c.v.getDate());
  return c.v;
};

export async function readWorkbook(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array", cellNF: true });
  const date1904 = !!wb.Workbook?.WBProps?.date1904;
  return wb.SheetNames.map((name) => {
    const ws = wb.Sheets[name];
    if (!ws || !ws["!ref"]) return { name, grid: [], cols: 0 };
    const R = XLSX.utils.decode_range(ws["!ref"]);
    const cols = R.e.c + 1;
    const grid = Array.from({ length: R.e.r + 1 }, (_, r) => Array.from({ length: cols }, (_, c) => cellValue(ws[XLSX.utils.encode_cell({ r, c })], date1904)));
    return { name, grid, cols };
  });
}

export const toNumber = (v) => {
  if (v == null || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (v instanceof Date) return null;
  let s = String(v).trim().replace(/[₺\sTL]/gi, "").replace(/%/g, "");
  if (!s) return null;
  if (/,\d{1,2}$/.test(s)) s = s.replace(/\./g, "").replace(",", ".");   // 1.372,50 → 1372.50
  else if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, "");      // 1.372 → 1372
  else s = s.replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const TR_MONTHS = { ocak: 1, şubat: 2, subat: 2, mart: 3, nisan: 4, mayıs: 5, mayis: 5, haziran: 6, temmuz: 7, ağustos: 8, agustos: 8, eylül: 9, eylul: 9, ekim: 10, kasım: 11, kasim: 11, aralık: 12, aralik: 12 };
const isoOf = (y, m, d) => {
  if (!(m >= 1 && m <= 12 && d >= 1 && d <= 31)) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};
const fullYear = (y) => (y.length === 2 ? 2000 + Number(y) : Number(y));
const localToday = () => { const d = new Date(); return isoOf(d.getFullYear(), d.getMonth() + 1, d.getDate()); };

/** Tarih hücresi → { iso, flag }. flag: null | "ambiguous" (gün/ay belirsiz, gg.aa varsayıldı) | "us" (aa/gg biçimi çevrildi) | "invalid" */
export function parseDateCell(v) {
  if (v == null || v === "") return { iso: null, flag: null };
  if (v instanceof Date) {
    if (Number.isNaN(v.getTime())) return { iso: null, flag: "invalid" };
    return { iso: isoOf(v.getUTCFullYear(), v.getUTCMonth() + 1, v.getUTCDate()), flag: null };
  }
  if (typeof v === "number") {
    if (v > 20000 && v < 80000) { const p = XLSX.SSF.parse_date_code(v); return { iso: p ? isoOf(p.y, p.m, p.d) : null, flag: p ? null : "invalid" }; }
    return { iso: null, flag: "invalid" };
  }
  const s = String(v).trim();
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) { const iso = isoOf(Number(m[1]), Number(m[2]), Number(m[3])); return { iso, flag: iso ? null : "invalid" }; }
  m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})\.?$/);
  if (m) {
    const a = Number(m[1]), b = Number(m[2]), y = fullYear(m[3]);
    if (a > 12 && b <= 12) return { iso: isoOf(y, b, a), flag: isoOf(y, b, a) ? null : "invalid" };          // 17.05.2026 (gg.aa)
    if (a <= 12 && b > 12) return { iso: isoOf(y, a, b), flag: isoOf(y, a, b) ? "us" : "invalid" };          // 05/17/2026 (aa/gg → çevrildi)
    if (a <= 12 && b <= 12) return { iso: isoOf(y, b, a), flag: a === b ? null : "ambiguous" };             // 05.06.2026 → gg.aa varsayıldı
    return { iso: null, flag: "invalid" };
  }
  m = s.match(/^(\d{1,2})\s+([a-zçğıöşü]+)\s+(\d{2,4})$/i);
  if (m && TR_MONTHS[lc(m[2])]) { const iso = isoOf(fullYear(m[3]), TR_MONTHS[lc(m[2])], Number(m[1])); return { iso, flag: iso ? null : "invalid" }; }
  return { iso: null, flag: "invalid" };
}
export const toISODate = (v) => parseDateCell(v).iso;
const fmtTR = (iso) => (iso ? iso.split("-").reverse().join(".") : "");

const UNIT_MAP = { a: "ADET", ad: "ADET", adet: "ADET", m: "METRE", mt: "METRE", metre: "METRE", mtl: "METRETÜL", paket: "PAKET", pk: "PAKET", kutu: "KUTU", kg: "KİLO", kilo: "KİLO", top: "TOP", rulo: "RULO", takım: "TAKIM", çift: "ÇİFT" };
export const normUnit = (v) => { const k = lc(v).replace(/\.$/, ""); if (!k) return ""; return UNIT_MAP[k] || String(v).trim().toLocaleUpperCase("tr"); };

const guessField = (h) => {
  const s = lc(h);
  if (!s) return "";
  if (s.includes("fiyat")) return "price";
  if (s.includes("tutar")) return "total";
  if (s.includes("isk")) return "disc";
  if (s.includes("miktar") || s === "adet" || s.includes("mik.")) return "qty";
  if (s.includes("birim")) return "unit";
  if (s.includes("tarih")) return "date";
  if (s.includes("kod") || s.includes("stok no")) return "code";
  if (s.includes("ürün") || s.includes("malzeme") || s.includes("açıklama") || s.includes("cins") || s.includes("mal ")) return "name";
  return "";
};
const colOf = (map, field) => { const e = Object.entries(map).find(([, f]) => f === field); return e ? Number(e[0]) : -1; };

/** İskonto sütunu doğrulaması: kalem satırlarında Miktar × Fiyat × (1 − isk) = Tutar denklemi kaç satırda tutuyor? (isk 0,62 ya da 62 biçimi) */
export function checkDiscountCol(grid, headerRow, map, ci) {
  const cn = colOf(map, "name"), cq = colOf(map, "qty"), cp = colOf(map, "price"), ct = colOf(map, "total");
  const out = { col: ci, values: 0, nz: 0, checked: 0, ok: 0, okNz: 0, fractionish: true, header: txt(grid[headerRow]?.[ci]) };
  for (let r = headerRow + 1; r < grid.length; r++) {
    const row = grid[r];
    if (cn < 0 || !txt(row[cn])) continue;
    const g = toNumber(row[ci]);
    if (g == null) continue;
    out.values++;
    if (g !== 0) out.nz++;
    if (Math.abs(g) > 1) out.fractionish = false;
    if (cq < 0 || cp < 0 || ct < 0) continue;
    const q = toNumber(row[cq]), p = toNumber(row[cp]), t = toNumber(row[ct]);
    if (q == null || p == null || t == null || !q || !p) continue;
    out.checked++;
    const base = q * p;
    if (Math.abs(base * (1 - g) - t) < 0.5 || Math.abs(base * (1 - g / 100) - t) < 0.5) { out.ok++; if (g !== 0) out.okNz++; }
  }
  return out;
}

/** Eşlemedeki iskonto sütununu (kullanıcı değiştirmiş olabilir) yeniden doğrular. */
export function refreshDiscCheck(grid, layout) {
  const dc = colOf(layout.map, "disc");
  if (dc < 0) return { ...layout, discCheck: null };
  const prev = layout.discCheck;
  return { ...layout, discCheck: { ...checkDiscountCol(grid, layout.headerRow, layout.map, dc), source: prev && prev.col === dc ? prev.source : "header" } };
}

/** Başlık satırı + sütun eşlemesi + iskonto sütunu tanıma/doğrulama (G sütunu öncelikli) + ödemeler bloğu + Excel özeti + iade blokları tespiti. */
export function detectLayout(grid) {
  let headerRow = -1, best = 0, map = {};
  for (let r = 0; r < Math.min(grid.length, 25); r++) {
    const m = {}; let score = 0;
    grid[r].forEach((cell, ci) => { const f = guessField(cell); if (f && !Object.values(m).includes(f)) { m[ci] = f; score++; } });
    if (Object.values(m).includes("name") && score > best) { best = score; headerRow = r; map = m; }
  }
  let discCheck = null;
  if (headerRow >= 0) {
    const dc = colOf(map, "disc");
    if (dc >= 0) discCheck = { ...checkDiscountCol(grid, headerRow, map, dc), source: "header" };
    else {
      const cols = grid[headerRow].length, used = new Set(Object.keys(map).map(Number));
      const cands = [6, ...Array.from({ length: cols }, (_, i) => i)].filter((ci, i, a) => ci < cols && !used.has(ci) && a.indexOf(ci) === i);
      for (const ci of cands) {
        const chk = checkDiscountCol(grid, headerRow, map, ci);
        if (!chk.values || !chk.nz) continue;
        const validated = chk.checked > 0 && chk.okNz >= 1 && chk.ok / chk.checked >= 0.6;
        const gUnverified = ci === 6 && chk.checked === 0 && chk.fractionish;   // G sütunu, tutar sütunu yok: 0–1 arası değerler → iskonto oranı
        if (validated || gUnverified) { map[ci] = "disc"; discCheck = { ...chk, source: "guess" }; break; }
      }
    }
  }
  const pay = { row: -1, dateCol: -1, amountCol: -1 };
  const summary = {};
  const returns = [];
  const itemCols = new Set(Object.keys(map).map(Number));
  for (let r = 0; r < grid.length; r++) {
    const row = grid[r];
    for (let ci = 0; ci < row.length; ci++) {
      const s = lc(row[ci]);
      if (!s || itemCols.has(ci)) continue;
      if (pay.row < 0 && /^ödemeler?$/.test(s)) {
        for (let rr = r + 1; rr <= Math.min(r + 3, grid.length - 1); rr++) {
          const hr = grid[rr];
          const dc = hr.findIndex((x, i) => i >= ci - 1 && lc(x).includes("tarih"));
          const ac = hr.findIndex((x, i) => i > dc && (lc(x).includes("miktar") || lc(x).includes("tutar")));
          if (dc >= 0 && ac >= 0) { pay.row = rr; pay.dateCol = dc; pay.amountCol = ac; break; }
        }
      }
      if (/^iade/.test(s)) {   // İADELER bloğu: alttaki metinler (tutarsız) — aktarılmaz, yalnızca raporlanır
        let blank = 0;
        for (let rr = r + 1; rr < grid.length && blank < 2; rr++) { const t = txt(grid[rr][ci]); if (!t) { blank++; continue; } blank = 0; returns.push({ excelRow: rr + 1, text: t }); }
      }
      const nextNum = row.slice(ci + 1).map(toNumber).find((x) => x != null);
      const label = s.replace(/\s+/g, " ");
      if (nextNum != null) {
        if (/toplam tutar|kdvsiz toplam|kdvsız toplam|genel toplam|ara toplam/.test(label) && summary.total == null) summary.total = nextNum;
        else if (/^kdv/.test(label) && summary.kdv == null) summary.kdv = nextNum;
        else if (/^ödeme$|ödenen|tahsilat/.test(label) && summary.paid == null) summary.paid = nextNum;
        else if (/bakiye|kalan/.test(label) && summary.balance == null) summary.balance = nextNum;
      }
    }
  }
  if (pay.row >= 0) { map[pay.dateCol] = "pay_date"; map[pay.amountCol] = "pay_amount"; }
  return { headerRow, map, payRow: pay.row, summary, returns, discCheck };
}

/** Kalem tablosunda ürün adı BOŞ ama Tutar hücresi dolu satırlar — aktarılmaz, fark analizinde raporlanır. */
export function findOrphanTotals(grid, layout) {
  const { map, headerRow } = layout;
  const cn = colOf(map, "name"), ct = colOf(map, "total"), pa = colOf(map, "pay_amount");
  if (cn < 0 || ct < 0) return [];
  const out = [];
  for (let r = headerRow + 1; r < grid.length; r++) {
    if (layout.payRow >= 0 && pa === ct && r > layout.payRow) break;
    const row = grid[r];
    if (txt(row[cn])) continue;
    const t = toNumber(row[ct]);
    if (t == null || Math.abs(t) < 0.005) continue;
    if (row.some((c) => typeof c === "string" && /toplam|bakiye|ödeme|kdv|devir/.test(lc(c)))) continue;
    out.push({ excelRow: r + 1, total: t });
  }
  return out;
}

const dateIssue = (flag, raw) => {
  if (flag === "invalid") return E(`Tarih anlaşılmadı: "${txt(raw)}"`);
  if (flag === "ambiguous") return W(`Tarih "${txt(raw)}" gün/ay belirsiz — gg.aa.yyyy varsayıldı`);
  if (flag === "us") return W(`Tarih "${txt(raw)}" ay/gün (ABD) biçiminde — çevrildi`);
  return null;
};

/** Kalemleri ayrıştır. opts: { discountMode: "auto"|"percent"|"fraction", fillDown } — eksik hücreler boş kalır, şüpheli veriler issues[] ile raporlanır. */
export function parseItems(grid, layout, opts = {}) {
  const { map, headerRow } = layout;
  const C = Object.fromEntries(["date", "name", "code", "unit", "qty", "price", "disc", "total"].map((f) => [f, colOf(map, f)]));
  if (C.name < 0) return [];
  const raw = [];
  for (let r = headerRow + 1; r < grid.length; r++) {
    const row = grid[r];
    const name = row[C.name];
    if (name == null || txt(name) === "") continue;
    raw.push({ r, row, name: txt(name) });
  }
  const discVals = raw.map(({ row }) => (C.disc >= 0 ? toNumber(row[C.disc]) : null)).filter((x) => x != null && x !== 0);
  const fraction = opts.discountMode === "fraction" || (opts.discountMode !== "percent" && discVals.length > 0 && discVals.every((x) => Math.abs(x) <= 1));
  const today = localToday();
  let lastDate = null, prevDated = null;
  return raw.map(({ r, row, name }, i) => {
    const issues = [];
    const dateRaw = C.date >= 0 ? row[C.date] : null;
    const { iso: d, flag } = parseDateCell(dateRaw);
    const di = dateIssue(flag, dateRaw); if (di) issues.push(di);
    if (!d && !di) issues.push(W("Tarih yok"));
    if (d) {
      if (prevDated && d < prevDated) issues.push(E(`Tarih sırası bozuk: önceki tarih ${fmtTR(prevDated)} iken bu satır ${fmtTR(d)} (gün/ay karışmış olabilir)`));
      if (d > today) issues.push(E(`Gelecek tarih: ${fmtTR(d)}`));
      if (Number(d.slice(0, 4)) < 2000) issues.push(E(`Çok eski tarih: ${fmtTR(d)}`));
      prevDated = d; lastDate = d;
    }
    const qtyRaw = C.qty >= 0 ? row[C.qty] : null;
    const qty = toNumber(qtyRaw);
    if (qty == null) issues.push(txt(qtyRaw) ? E(`Miktar anlaşılmadı: "${txt(qtyRaw)}"`) : W("Miktar yok"));
    else if (qty < 0) issues.push(E(`Negatif miktar: ${qty}`));
    const discRaw = C.disc >= 0 ? row[C.disc] : null;
    let disc = toNumber(discRaw);
    if (disc == null && txt(discRaw)) issues.push(E(`İskonto anlaşılmadı: "${txt(discRaw)}"`));
    if (disc != null && fraction) disc = disc * 100;
    if (disc != null) { disc = Math.round(disc * 100) / 100; if (disc < 0 || disc > 100) issues.push(E(`İskonto aralık dışı: %${disc}`)); }
    const priceRaw = C.price >= 0 ? row[C.price] : null;
    let price = toNumber(priceRaw);
    if (price == null && txt(priceRaw)) issues.push(E(`Fiyat anlaşılmadı: "${txt(priceRaw)}"`));
    else if (price != null && price < 0) issues.push(E(`Negatif fiyat: ${price}`));
    const total = C.total >= 0 ? toNumber(row[C.total]) : null;
    let derived = false;
    if (price == null && total != null && total !== 0 && qty) { price = Math.round((total / qty / (1 - (disc || 0) / 100)) * 10000) / 10000; derived = true; issues.push(I(`Fiyat Excel'deki tutardan türetildi (${total} ÷ ${qty})`)); }
    if (price == null) issues.push(W("Fiyat yok"));
    else if (total != null && qty != null) {
      const calc = qty * price * (1 - (disc || 0) / 100);
      if (Math.abs(calc - total) > 0.5) issues.push(E(`Excel tutarı ${fmtNum(total)} ile hesaplanan ${fmtNum(calc)} uyuşmuyor (fark ${fmtNum(calc - total)})`));
    }
    const unitRaw = C.unit >= 0 ? row[C.unit] : null;
    const unit = txt(unitRaw) ? normUnit(unitRaw) : "";
    if (!unit) issues.push(W("Birim yok"));
    else if (lc(unitRaw) !== lc(unit) && UNIT_MAP[lc(unitRaw).replace(/\.$/, "")]) issues.push(I(`Birim "${txt(unitRaw)}" → ${unit} olarak yorumlandı`));
    return {
      key: `${r}`, excelRow: r + 1, seq: i + 1, include: true, removed: false, isBalanceRow: /bakiye|devir/.test(lc(name)),
      date: d || (opts.fillDown ? lastDate : null), dateFilled: !d && !!(opts.fillDown && lastDate), origDate: d,
      name, code: C.code >= 0 && row[C.code] != null ? txt(row[C.code]) : "",
      unit, quantity: qty == null ? "" : qty, unit_price: price == null ? "" : price, priceDerived: derived,
      discount_rate: disc == null ? "" : disc, excelTotal: total, noPrice: price == null, issues, _i: i,
    };
  });
}

/** Boş tarihleri önceki satırın tarihiyle doldur / geri al (düzenlenmiş satırlar korunur). */
export function applyFillDown(items, on) {
  let last = null;
  return items.map((it) => {
    if (it.origDate || (it.date && !it.dateFilled)) { last = it.date; return it; }
    if (on) return { ...it, date: last, dateFilled: !!last };
    return { ...it, date: null, dateFilled: false };
  });
}

/** Excel satır sırasını koruyarak ardışık aynı-tarihli satırları tek kayda gruplar; boş tarih boş kalır, sıralama için sort_date kullanılır. */
export function groupEntries(items, mapItem) {
  const dated = items.map((i) => i.date || "");
  const firstDate = dated.find(Boolean) || "";
  const groups = [];
  let lastKnown = firstDate;
  items.forEach((it, idx) => {
    const d = dated[idx];
    if (d) lastKnown = d;
    const prev = groups[groups.length - 1];
    if (prev && prev.date === d) prev.items.push(mapItem(it));
    else groups.push({ date: d, sort_date: d || lastKnown, seq: idx + 1, items: [mapItem(it)], note: "Excel aktarımı" });
  });
  return groups;
}

export function parsePayments(grid, layout) {
  const dc = colOf(layout.map, "pay_date"), ac = colOf(layout.map, "pay_amount");
  if (dc < 0 || ac < 0) return [];
  const start = layout.payRow >= 0 ? layout.payRow + 1 : layout.headerRow + 1;
  const today = localToday();
  const out = []; let blank = 0, prev = null;
  for (let r = start; r < grid.length; r++) {
    const row = grid[r];
    const amt = toNumber(row[ac]);
    const dv = row[dc];
    if (amt == null) { if (dv == null || (typeof dv === "string" && /iade|not/.test(lc(dv)))) { blank++; if (blank >= 3 && out.length) break; } continue; }
    blank = 0;
    const { iso: d, flag } = parseDateCell(dv);
    const issues = [];
    const note = !d && txt(dv) ? txt(dv) : "";
    if (note) issues.push(E(`Tarih yerine "${note}" yazıyor — ${amt} ₺ ödeme olarak alındı, kontrol edin`));
    else { const di = dateIssue(flag, dv); if (di) issues.push(di); else if (!d) issues.push(W("Tarih yok")); }
    if (d) { if (prev && d < prev) issues.push(E(`Ödeme tarihi sırası bozuk: ${fmtTR(prev)} → ${fmtTR(d)}`)); if (d > today) issues.push(E(`Gelecek tarih: ${fmtTR(d)}`)); prev = d; }
    if (amt < 0) issues.push(E(`Negatif ödeme: ${amt}`));
    out.push({ key: `p${r}`, excelRow: r + 1, include: true, removed: false, date: d, dateFilled: false, amount: Math.abs(amt), excelAmount: Math.abs(amt), note, issues });
  }
  return out;
}

export const lineTotal = (it) => (Number(it.quantity) || 0) * (Number(it.unit_price) || 0) * (1 - (Number(it.discount_rate) || 0) / 100);
export const near = (a, b) => a != null && b != null && Math.abs(Number(a) - Number(b)) < 0.05;
const nz = (n) => Math.abs(n) >= 0.005;
const sumOf = (rows) => rows.reduce((s, r) => s + r.amount, 0);
const pushPart = (parts, key, text, rows) => { if (rows.length) parts.push({ key, text, amount: sumOf(rows), rows }); };
const buildDiff = (calc, excel, parts) => { const diff = (calc || 0) - excel; return { calc: calc || 0, excel, diff, parts, unexplained: diff - sumOf(parts) }; };

/**
 * FARK ANALİZİ: Excel özeti (TOPLAM / ÖDEME / BAKİYE) ile hesaplanan arasındaki farkı kaynaklarına ayırır.
 * Kalem farkı = satır bazlı (Miktar×Fiyat×İsk ≠ Excel tutarı) + devir − aktarım dışı − silinen − adı boş satırlar − Excel'in kendi iç tutarsızlığı.
 */
export function explainDifference(items, pays, ctx = {}) {
  const { summary: S = {}, totals: T = {}, orphans = [], vatExempt } = ctx;
  const opening = Number(ctx.opening) || 0;
  const excelOf = (i) => (i.excelTotal == null ? 0 : Number(i.excelTotal));
  const res = {};
  if (S.total != null) {
    const parts = [];
    pushPart(parts, "rows", "Satırda Miktar × Fiyat × İskonto ile Excel tutarı farklı", items.filter((i) => i.include).map((i) => {
      const calc = lineTotal(i);
      return { excelRow: i.excelRow, label: i.name, amount: calc - excelOf(i), detail: i.excelTotal == null ? `Excel'de tutar yok → hesaplanan ${fmtNum(calc)}` : `Excel ${fmtNum(i.excelTotal)} → hesaplanan ${fmtNum(calc)}` };
    }).filter((x) => nz(x.amount)));
    if (nz(opening)) parts.push({ key: "opening", text: "Devir bakiyesi eklendi", amount: opening, rows: [] });
    pushPart(parts, "excluded", "Aktarım dışı bıraktığınız satırlar", items.filter((i) => !i.include && !i.removed).map((i) => ({ excelRow: i.excelRow, label: i.name, amount: -excelOf(i) })).filter((x) => nz(x.amount)));
    pushPart(parts, "removed", "Önizlemede sildiğiniz satırlar", items.filter((i) => i.removed).map((i) => ({ excelRow: i.excelRow, label: i.name, amount: -excelOf(i) })).filter((x) => nz(x.amount)));
    pushPart(parts, "orphans", "Excel'de ürün adı boş ama tutarı olan satırlar (aktarılmadı)", orphans.map((o) => ({ excelRow: o.excelRow, label: "(ürün adı boş)", amount: -o.total })));
    const colSum = items.reduce((s, i) => s + excelOf(i), 0) + orphans.reduce((s, o) => s + o.total, 0);
    const self = S.total - colSum;
    if (nz(self)) parts.push({ key: "excel", text: `Excel'in kendi özeti (${fmtNum(S.total)}) ile Tutar sütununun toplamı (${fmtNum(colSum)}) farklı — Excel formülü bazı satırları kapsamıyor / fazladan kapsıyor olabilir`, amount: -self, rows: [] });
    res.total = buildDiff(T.net, S.total, parts);
  }
  if (S.paid != null) {
    const parts = [];
    const ex = (p) => Number(p.excelAmount) || 0;
    pushPart(parts, "prows", "Tutarı düzenlenen / elle eklenen ödemeler", pays.filter((p) => p.include).map((p) => ({ excelRow: p.excelRow, label: p.excelAmount == null ? "elle eklenen ödeme" : p.note || "ödeme", amount: (Number(p.amount) || 0) - ex(p), detail: p.excelAmount == null ? "" : `Excel ${fmtNum(p.excelAmount)} → ${fmtNum(p.amount)}` })).filter((x) => nz(x.amount)));
    pushPart(parts, "pexcluded", "Aktarım dışı bıraktığınız ödemeler", pays.filter((p) => !p.include && !p.removed).map((p) => ({ excelRow: p.excelRow, label: p.note || "ödeme", amount: -ex(p) })).filter((x) => nz(x.amount)));
    pushPart(parts, "premoved", "Önizlemede sildiğiniz ödemeler", pays.filter((p) => p.removed).map((p) => ({ excelRow: p.excelRow, label: p.note || "ödeme", amount: -ex(p) })).filter((x) => nz(x.amount)));
    const colSum = pays.reduce((s, p) => s + ex(p), 0);
    const self = S.paid - colSum;
    if (nz(self)) parts.push({ key: "pexcel", text: `Excel'in kendi özeti (${fmtNum(S.paid)}) ile ÖDEMELER sütununun toplamı (${fmtNum(colSum)}) farklı`, amount: -self, rows: [] });
    res.paid = buildDiff(T.paid, S.paid, parts);
  }
  if (S.balance != null) {
    const parts = [];
    const k = vatExempt ? 1 : 1.2;
    const dT = res.total ? res.total.diff : 0, dP = res.paid ? res.paid.diff : 0;
    if (nz(dT)) parts.push({ key: "bt", text: vatExempt ? "Toplam tutar farkından" : "Toplam tutar farkından (KDV dahil)", amount: dT * k, rows: [] });
    if (nz(dP)) parts.push({ key: "bp", text: "Ödeme farkından", amount: -dP, rows: [] });
    if (S.total != null && S.paid != null) {
      const expected = S.total * k - S.paid;
      if (nz(expected - S.balance)) parts.push({ key: "bexcel", text: `Excel özetinde Bakiye (${fmtNum(S.balance)}) ≠ Toplam${k > 1 ? " + KDV" : ""} − Ödeme (${fmtNum(expected)}) — Excel'in kendi içinde tutarsız`, amount: expected - S.balance, rows: [] });
    }
    res.balance = buildDiff(T.balance, S.balance, parts);
  }
  return res;
}

/** Uyarı özeti: [{ key, sev, text, rows:[{excelRow, label, detail}] }] — error (şüpheli/anlaşılmadı) > warn (eksik) > info (yorumlandı). */
export function collectIssues(items, pays, ctx = {}) {
  const live = items.filter((i) => !i.removed), plive = pays.filter((p) => !p.removed);
  const inc = live.filter((i) => i.include), pinc = plive.filter((p) => p.include);
  const out = [];
  const bucket = (key, sev, text, rows) => { if (rows.length) out.push({ key, sev, text: typeof text === "function" ? text(rows.length) : text, rows }); };
  const pick = (list, pred, label) => list.filter((x) => (x.issues || []).some(pred)).map((x) => ({ excelRow: x.excelRow, label: label(x), detail: (x.issues || []).filter(pred).map((s) => s.text).join(" · ") }));
  const L = (x) => x.name || `${x.amount} ₺ ödeme`;
  bucket("bad", "error", (n) => `${n} satırda anlaşılmayan hücre var — bu değerler boş bırakıldı`, pick([...inc, ...pinc], (s) => s.sev === "error" && s.text.includes("anlaşılmadı"), L));
  bucket("paytext", "error", (n) => `${n} ödeme satırında tarih yerine metin yazıyor`, pick(pinc, (s) => s.text.startsWith("Tarih yerine"), L));
  bucket("order", "error", (n) => `${n} satırda tarih sırası bozuk / gelecek / çok eski tarih — gün-ay karışıklığı olabilir, kontrol edin`, pick([...inc, ...pinc], (s) => /sırası bozuk|Gelecek tarih|Çok eski/.test(s.text), L));
  bucket("total", "error", (n) => `${n} satırda Excel tutarı ile miktar×fiyat uyuşmuyor`, pick(inc, (s) => s.text.includes("uyuşmuyor"), L));
  bucket("range", "error", (n) => `${n} satırda negatif / aralık dışı değer var`, pick([...inc, ...pinc], (s) => /Negatif|aralık dışı/.test(s.text), L));
  if (ctx.discCheck) {
    const D = ctx.discCheck, Lt = colLetter(D.col);
    if (D.source === "guess") out.push({ key: "discguess", sev: D.checked ? "info" : "warn", text: `${Lt} sütunu ${D.header ? `("${D.header}")` : "başlıksız"} — değerleri İSKONTO olarak tanındı${D.checked ? ` · ${D.ok}/${D.checked} satırda Miktar × Fiyat × (1−İsk) = Tutar doğrulandı` : " · Tutar sütunu olmadığı için doğrulanamadı, kontrol edin"}`, rows: [] });
    else if (D.checked > 0 && D.ok / D.checked < 0.6) out.push({ key: "disccheck", sev: "error", text: `İskonto sütunu (${Lt}) ${D.checked - D.ok}/${D.checked} satırda tutarla uyuşmuyor — iskonto biçimini (yüzde / oran) ve sütunu kontrol edin`, rows: [] });
    else if (D.checked > 0) out.push({ key: "discok", sev: "info", text: `İskonto sütunu (${Lt}) ${D.ok}/${D.checked} satırda Miktar × Fiyat × (1−İsk) = Tutar ile doğrulandı`, rows: [] });
  }
  if (ctx.summary) {
    const S = ctx.summary, T = ctx.totals || {}, D = ctx.diff || {};
    const sgn = (n) => `${n > 0 ? "+" : n < 0 ? "−" : ""}${fmtNum(Math.abs(n))}`;
    const rows = [];
    const line = (label, calc, excel, d) => rows.push({ excelRow: null, label, detail: `Excel ${fmtNum(excel)} · hesaplanan ${fmtNum(calc)} · FARK ${sgn(d ? d.diff : (calc || 0) - excel)}${d && d.parts.length ? ` (kaynak: ${d.parts.map((p) => `${p.text.split(" — ")[0]} ${sgn(p.amount)}`).join("; ")})` : ""}` });
    if (S.total != null && !near(T.net, S.total)) line(ctx.vatExempt ? "Toplam Tutar" : "KDV'siz Toplam", T.net, S.total, D.total);
    if (S.paid != null && !near(T.paid, S.paid)) line("Ödemeler", T.paid, S.paid, D.paid);
    if (S.balance != null && !near(T.balance, S.balance)) line("Kalan Bakiye", T.balance, S.balance, D.balance);
    const excluded = live.length !== inc.length || plive.length !== pinc.length || items.length !== live.length || pays.length !== plive.length;
    bucket("summary", excluded ? "warn" : "error", excluded
      ? "Excel özeti ile hesaplanan toplamlar farklı — bazı satırları aktarım dışı bıraktığınız / sildiğiniz için bu fark bekleniyor (Fark Analizi'ne bakın)"
      : "Excel'deki hesap özeti ile hesaplanan toplamlar uyuşmuyor — farkın kaynağı Fark Analizi'nde satır satır listelendi", rows);
  }
  bucket("date", "warn", (n) => `${n} kalemin tarihi yok — tarih BOŞ bırakılır, deftere Excel satır sırasına göre işlenir`, pick(inc, (s) => s.text === "Tarih yok", L));
  bucket("datefmt", "warn", (n) => `${n} tarih gün/ay belirsiz veya ABD biçiminde — çevrimi kontrol edin`, pick([...inc, ...pinc], (s) => /belirsiz|ABD/.test(s.text), L));
  bucket("qty", "warn", (n) => `${n} kalemin miktarı yok — boş (0) aktarılır`, pick(inc, (s) => s.text === "Miktar yok", L));
  bucket("price", "warn", (n) => `${n} kalemin fiyatı yok — boş (0 ₺) aktarılır, defterde sonra girebilirsiniz`, pick(inc, (s) => s.text === "Fiyat yok", L));
  bucket("unit", "warn", (n) => `${n} kalemin birimi yok — boş bırakılır`, pick(inc, (s) => s.text === "Birim yok", L));
  bucket("paydate", "warn", (n) => `${n} ödemenin tarihi yok — boş bırakılır`, pick(pinc, (s) => s.text === "Tarih yok", L));
  if (ctx.orphans?.length) out.push({ key: "orphans", sev: "warn", text: `Excel'de ${ctx.orphans.length} satırda ürün adı boş ama tutar var — bu satırlar AKTARILMADI (ürün adını Excel'de yazıp yeniden yükleyin)`, rows: ctx.orphans.map((o) => ({ excelRow: o.excelRow, label: "(ürün adı boş)", detail: `${fmtNum(o.total)} ₺` })) });
  if (ctx.returns?.length) out.push({ key: "returns", sev: "info", text: `Excel'de "İADELER" listesi var (${ctx.returns.length} satır) — tutarı olmadığı için AKTARILMADI, gerekirse elle işleyin`, rows: ctx.returns.map((x) => ({ excelRow: x.excelRow, label: x.text, detail: "" })) });
  bucket("derived", "info", (n) => `${n} kalemin fiyatı Excel'deki tutardan türetildi (tutar ÷ miktar)`, pick(inc, (s) => s.text.startsWith("Fiyat Excel"), L));
  bucket("units", "info", (n) => `${n} kalemde birim kısaltması yorumlandı (a→ADET, m→METRE …)`, pick(inc, (s) => s.text.includes("olarak yorumlandı"), L));
  return out;
}
